import { Action } from 'redux';
import { select, put } from 'redux-saga/effects';
import { PostcodeModel, HybrisCustomerDetailsModel, AddressModel } from 'models';
import { BasketErrorModel } from 'models/Errors';
import { BasketActionTypes, BasketActions } from '../';
import { AppState, ErrorAction } from 'store';
import { userHelper, ACTION_CREATOR_NAME, deliveryHelper, errorToBasketErrorModel } from 'helpers';
import { api } from 'repositories/api';
import { initBasketPageSaga } from './initBasketPage';
import { validateBasketReservationsSaga } from './validation/validateBasketReservations';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';
import { validateCartSaga } from './validation/validateCart';

const RESERVATION_ERROR_MESSAGE = 'Reservation has expired. Please apply again';

export interface CalculateDeliveryCostAction extends Action<BasketActionTypes.CALCULATE_DELIVERY_COST> {
    postcode: PostcodeModel;
}

export interface CalculateDeliveryCostActionStart extends Action<BasketActionTypes.CALCULATE_DELIVERY_COST_START> {
    postcode: PostcodeModel;
}

export interface CalculateDeliveryCostActionSuccess extends Action<BasketActionTypes.CALCULATE_DELIVERY_COST_SUCCESS> {
}

export interface CalculateDeliveryCostActionError extends ErrorAction<BasketActionTypes.CALCULATE_DELIVERY_COST_ERROR> {
}

export const calculateDeliveryCost: (postcode: PostcodeModel) => CalculateDeliveryCostAction = (postcode: PostcodeModel) => ({
    type: BasketActionTypes.CALCULATE_DELIVERY_COST,
    postcode
});
calculateDeliveryCost[ACTION_CREATOR_NAME] = 'calculateDeliveryCost';

export type CalculateDeliveryCostFunc = (...args: Parameters<typeof calculateDeliveryCost>) => void;

export function* calculateDeliveryCostSaga({ postcode }: CalculateDeliveryCostAction) {
    yield put<BasketActions>({
        type: BasketActionTypes.CALCULATE_DELIVERY_COST_START,
        postcode
    });
}

export function* calculateDeliveryCostQueueSaga({ postcode }: CalculateDeliveryCostActionStart): BasketSagaReturnType {
    try {
        // do reservation validation also before calculate delivery cost api call
        // because this call removes (in background) expired items from the basket
        // therefore it won't be possible to catch the reservation expiration event from initBasketSaga
        try {
            yield callBasketSaga(validateBasketReservationsSaga, {
                type: BasketActionTypes.VALIDATE_BASKET_RESERVATIONS,
                isBasketPage: true
            });
        } catch {
            // this saga call could be failed if previous saved postcode is invalid, but saved
            // it's because of bug in api: system saves invalid (for example A11AA) postcodes
            // TODO: remove ignoring errors after api fixed
        }

        let state: AppState = yield select();

        if (state.basketState.shouldShowReservationWarning) {
            throw new BasketErrorModel(RESERVATION_ERROR_MESSAGE, undefined, 'calculateDeliveryCostQueueSaga');
        }

        const prevPostcode = state.basketState.basket!.deliveryAddress?.postcode ?? null;
        yield deliveryHelper.calculateDeliveryCost(prevPostcode, postcode, userHelper.getCurrentUserName(), state.basketState.basket!.guid);

        yield clearDeliveryAddress(postcode);

        yield callBasketSaga(initBasketPageSaga, {
            type: BasketActionTypes.INIT_BASKET_PAGE
        });

        yield callBasketSaga(validateCartSaga, {
            type: BasketActionTypes.VALIDATE_CART,
            isBasketPage: true
        });

        yield put<BasketActions>({
            type: BasketActionTypes.CALCULATE_DELIVERY_COST_SUCCESS
        });

        return true;
    } catch (errors) {
        yield put<BasketActions>({
            type: BasketActionTypes.CALCULATE_DELIVERY_COST_ERROR,
            error: Array.isArray(errors)
                ? errorToBasketErrorModel(errors[0], 'calculateDeliveryCostQueueSaga')
                : errorToBasketErrorModel(errors, 'calculateDeliveryCostQueueSaga')
        });

        return false;
    }
}

function* clearDeliveryAddress(postcode: PostcodeModel) {
    let state: AppState = yield select();
    let previousPostcode = state.basketState.basket!.deliveryAddress?.postcode;

    if (!postcode.isEqual(previousPostcode)) {
        let currentAddress = state.basketState.basket!.deliveryAddress;
        let isDeliveryAddressSet = currentAddress?.line1 || currentAddress?.town;

        if (isDeliveryAddressSet) {
            let userName = state.basketState.basket!.user.name;
            let customerId = state.basketState.basket!.session.userId;
            const emptyAddress = new AddressModel('', '', null, '', '', '', '', postcode.code);
            let hybrisCustomerDetails: HybrisCustomerDetailsModel = {
                deliveryAddress: null,
                billingAddress: null,
                email: currentAddress!.email,
                phone: currentAddress!.phone,
                firstName: currentAddress!.firstName,
                surname: currentAddress!.surname,
                title: currentAddress!.title,
                customerId: customerId,
                isCustomerInfoExists: true
            };

            yield api.hybris.updateCustomerDeliveryDetails(userName, state.basketState.basket!.guid, hybrisCustomerDetails, emptyAddress);
        }
    }
}